import { useAuth } from "@clerk/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiRequestError, fetchWithAuthRetry } from "./api.js";
import { blockRemoveApiPath, blockRemoveFailure } from "./rd-block-time.js";
import { RD_SCHEDULE_WEEK_QUERY_PREFIX } from "./rd-schedule-week.js";

/**
 * NO-877: DELETE /api/rd-schedule/blocks/:id. The answer is 204 with no body, so
 * it reads the status itself. A 404 can mean the block is already gone or that
 * blocked time is unavailable; either way the Week is refreshed so it shows
 * what is stored, and the dialog's sentence says which.
 */
export function useRemoveRdTimeBlock(rdId: string | null) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const refreshWeeks = () =>
    queryClient.invalidateQueries({ queryKey: RD_SCHEDULE_WEEK_QUERY_PREFIX });

  return useMutation({
    mutationFn: async (blockId: string) => {
      const response = await fetchWithAuthRetry(
        getToken,
        blockRemoveApiPath(blockId, rdId),
        {
          method: "DELETE",
        }
      );

      if (response.ok) return;

      const body: unknown = await response.json().catch(() => undefined);

      throw new ApiRequestError(
        `Request failed with ${response.status}`,
        response.status,
        body
      );
    },
    onSuccess: refreshWeeks,
    onError: (error) => {
      if (error instanceof ApiRequestError && error.status === 404) void refreshWeeks();
    },
  });
}

export function removeFailureOf(error: unknown): string {
  return error instanceof ApiRequestError
    ? blockRemoveFailure(error.status, error.body)
    : blockRemoveFailure(null);
}
